// Display state for each compliance screen: badge tone + caption. The raw
// numbers come straight from the engine; this module only decides how a screen
// presents. Shared by the Checks stage board, the Summary's compare tables, the
// CFO view, and PDF export, so every surface says the same thing about a plan.
//
// Everything here is an informal, modeled screen. Never a formal test result.

import { formatCurrency, formatPercent } from './utils.js'

export const buildAdpScreen = (metrics) => {
  if (!metrics?.informalAdpAvailable) return { badge: null, title: 'Both HCE and NHCE records with usable compensation are required.' }
  const limitDetail = `HCE ${formatPercent(metrics.informalAdpHCE)} vs basic ${formatPercent(metrics.informalAdpLimit)} limit (NHCE ${formatPercent(metrics.informalAdpNHCE)})`
  if (metrics.informalAdpDeemedSatisfied) {
    return { badge: { tone: 'good', text: 'Safe-harbor treatment modeled' }, title: `${limitDetail} · confirm the plan's safe-harbor terms and operation` }
  }
  if (metrics.informalAdpStatus === 'over-basic-threshold') {
    const correction = metrics.informalAdpCorrectionEstimate > 0
      ? ` · est. corrective distribution ${formatCurrency(metrics.informalAdpCorrectionEstimate)}`
      : ''
    return { badge: { tone: 'warning', text: 'Potential ADP issue · review' }, title: `${limitDetail}${correction}` }
  }
  return { badge: { tone: 'good', text: 'Below modeled basic limit' }, title: limitDetail }
}

export const buildAcpScreen = (metrics) => {
  if (!metrics?.informalAcpAvailable) return { badge: null, title: 'Both HCE and NHCE records with usable compensation are required.' }
  const limitDetail = `HCE ${formatPercent(metrics.informalAcpHCE)} vs basic ${formatPercent(metrics.informalAcpLimit)} limit (match + after-tax)`
  if (metrics.informalAcpFullyDeemedSatisfied) {
    return { badge: { tone: 'good', text: 'Safe-harbor treatment modeled' }, title: `${limitDetail} · confirm the plan's safe-harbor terms and operation` }
  }
  if (metrics.informalAcpMatchDeemedSatisfied && metrics.informalAcpAfterTaxPresent) {
    return { badge: { tone: 'warning', text: 'After-tax ACP review' }, title: `${limitDetail} · the model treats after-tax contributions as ACP-tested even when safe-harbor match treatment may apply` }
  }
  if (metrics.informalAcpStatus === 'over-basic-threshold') {
    const correction = metrics.informalAcpCorrectionEstimate > 0
      ? ` · est. corrective distribution ${formatCurrency(metrics.informalAcpCorrectionEstimate)}`
      : ''
    return { badge: { tone: 'warning', text: 'Potential ACP issue · review' }, title: `${limitDetail}${correction}` }
  }
  return { badge: { tone: 'good', text: 'Below modeled basic limit' }, title: limitDetail }
}

export const buildCoverageScreen = (metrics) => {
  if (!metrics?.informalCoverageAvailable) {
    // Distinguish "nothing to test" (no employer money allocated) from "can't run
    // it" (population too small) – a zero-employer plan is not a coverage failure.
    const title = metrics?.informalCoverageUnavailableReason === 'no-employer-contributions'
      ? 'No employer contributions are allocated, so there is no employer benefit to test for coverage. (Elective-deferral coverage is separate and not shown here.)'
      : 'Both HCE and NHCE non-excludable populations are required.'
    return { badge: { tone: 'neutral', text: 'Not applicable' }, title }
  }
  const base = `NHCE ${formatPercent(metrics.informalCoverageNHCEBenefitRate)} / HCE ${formatPercent(metrics.informalCoverageHCEBenefitRate)} benefiting`
  if (metrics.informalCoverageStatus === 'under-basic-threshold') {
    return metrics.informalCoverageAvgBenefitMayPass
      ? { badge: { tone: 'warning', text: 'Below 70% · ABT review' }, title: `${base} · average-benefit ratio ${formatPercent(metrics.informalCoverageAvgBenefitRatio)}` }
      : { badge: { tone: 'danger', text: 'Potential coverage issue' }, title: `${base} · flagged for professional review` }
  }
  return { badge: { tone: 'good', text: 'Modeled ratio ≥ 70%' }, title: base }
}

export const buildTopHeavyScreen = (metrics) => {
  if (!metrics?.topHeavyAvailable) {
    return {
      badge: null,
      title: metrics?.topHeavyUnavailableReason === 'missing-key-employee-data'
        ? `${metrics.topHeavyMissingKeyCount} employee${metrics.topHeavyMissingKeyCount === 1 ? '' : 's'} missing key status`
        : 'No balance data',
    }
  }
  const title = `Key employee ${formatCurrency(metrics.topHeavyKeyBalance || 0)} / total ${formatCurrency(metrics.topHeavyTotalBalance || 0)}`
  if (!metrics.isTopHeavy) return { badge: { tone: 'good', text: 'Below modeled 60% threshold' }, title }
  return {
    badge: { tone: metrics.topHeavyRatio >= 80 ? 'danger' : 'warning', text: 'Potential top-heavy issue' },
    title: `${title} · flagged for professional review`,
  }
}

export const buildTopHeavyMinScreen = (metrics) => {
  if ((!metrics?.topHeavyAvailable && !metrics?.cashBalanceEnabled) || !metrics?.isTopHeavy) {
    return { badge: { tone: 'neutral', text: 'No modeled top-heavy flag' }, title: 'This model does not indicate a top-heavy minimum; confirm with the plan administrator.' }
  }
  const title = metrics.topHeavyMinSatisfiedBySafeHarbor
    ? `The modeled ${formatPercent(metrics.topHeavyMinPercent)} non-key minimum appears covered by the safe-harbor nonelective; confirm plan terms and operation`
    : `Modeled ${formatPercent(metrics.topHeavyMinPercent)} minimum · ${formatCurrency(metrics.topHeavyMinProvidedTotal || 0)} provided of ${formatCurrency(metrics.topHeavyMinRequiredTotal || 0)} across ${metrics.topHeavyMinEligibleCount || 0} eligible non-key participants`
  const badge = metrics.topHeavyMinSatisfiedBySafeHarbor
    ? { tone: 'good', text: 'Safe-harbor coverage modeled' }
    : metrics.topHeavyMinShortfall > 0
      ? { tone: 'warning', text: 'Potential minimum shortfall' }
      : { tone: 'good', text: 'No modeled shortfall' }
  return { badge, title }
}

export const buildCrossTestingScreen = (metrics) => {
  const ct = metrics?.crossTesting
  if (!ct?.available) {
    return { badge: { tone: 'neutral', text: 'Not applicable' }, title: 'Applies once the plan allocates employer nonelective/profit-sharing money to test on a benefits basis.' }
  }
  if (ct.status === 'gateway-not-met') {
    return {
      badge: { tone: 'warning', text: 'Potential gateway shortfall' },
      title: `${ct.gatewayShortfallCount} NHCE${ct.gatewayShortfallCount === 1 ? '' : 's'} below the ${formatPercent(ct.gatewayRate)} minimum allocation gateway · ${formatCurrency(ct.gatewayShortfallTotal || 0)} to close`,
    }
  }
  if (ct.status === 'passes') {
    return {
      badge: { tone: 'good', text: 'No modeled rate-group flag' },
      title: `The model did not flag any of the ${ct.rateGroups?.length || 0} rate group${(ct.rateGroups?.length || 0) === 1 ? '' : 's'} under its §410(b) assumptions (modeled gateway ${formatPercent(ct.gatewayRate)}). This is not a formal test.`,
    }
  }
  return {
    badge: { tone: 'warning', text: 'Rate groups flagged for review' },
    title: `The model flags ${ct.failingRateGroupCount} rate group${ct.failingRateGroupCount === 1 ? '' : 's'} for §410(b) review on a benefits basis. This is not a formal test.`,
  }
}

export const buildCashBalanceAllocationScreen = (metrics) => {
  if (!metrics?.cashBalanceEnabled || !metrics?.totalCashBalanceCredits) {
    return { badge: { tone: 'neutral', text: 'No CB credits' }, title: 'No cash-balance credits are modeled.' }
  }
  const hceShare = formatPercent(metrics.cashBalanceHceCreditShare || 0)
  if (metrics.cashBalanceAllocationStatus === 'hce-only') {
    return {
      badge: { tone: 'danger', text: 'Potential coverage issue' },
      title: `${metrics.cashBalanceHceOrKeyCreditCount || 0} HCE/key employee${metrics.cashBalanceHceOrKeyCreditCount === 1 ? '' : 's'} receive cash-balance credits; 0 of ${metrics.cashBalanceEligibleNhceCount || 0} eligible NHCEs receive one. This pattern is flagged for DB coverage/nondiscrimination review with the TPA or actuary.`,
    }
  }
  if (metrics.cashBalanceAllocationStatus === 'hce-concentrated') {
    return {
      badge: { tone: 'warning', text: 'Concentration flagged' },
      title: `HCEs receive ${hceShare} of cash-balance dollars. Retira.app flags this as directional only; confirm DB coverage, gateway, and cross-testing with the TPA or actuary.`,
    }
  }
  return {
    badge: { tone: 'neutral', text: 'Directional pattern only' },
    title: `HCEs receive ${hceShare} of cash-balance dollars; ${metrics.cashBalanceEligibleNhceCreditCount || 0} eligible NHCE${metrics.cashBalanceEligibleNhceCreditCount === 1 ? '' : 's'} receive a cash-balance credit. Directional screen only.`,
  }
}

export const buildMinimumParticipationScreen = (metrics) => {
  if (!metrics?.minimumParticipationAvailable) {
    const reason = metrics?.minimumParticipationUnavailableReason
    return {
      badge: { tone: 'neutral', text: 'Not available' },
      title: reason === 'missing-age' || reason === 'invalid-inputs'
        ? 'Every cash-balance participant needs a date of birth and compensation before minimum participation can be screened.'
        : reason === 'missing-apr-for-age'
          ? 'At least one participant falls outside the anchored annuity-factor ages, so the accrual rate cannot be computed.'
          : 'Minimum participation cannot be screened from the data on file.',
    }
  }
  const { minimumParticipationBenefitingCount: benefiting, minimumParticipationRequiredCount: required } = metrics
  const title = `${benefiting} of ${metrics.minimumParticipationNonExcludableCount} non-excludable participants receive a modeled meaningful benefit (at least ${formatPercent(metrics.minimumParticipationMeaningfulRatePercent)} accrual); the model's review threshold is ${required}. Modeled at testing age ${metrics.minimumParticipationTestingAge}, not a formal test.`
  if (!metrics.minimumParticipationPasses) {
    return {
      badge: { tone: 'danger', text: 'Potential shortfall · review' },
      title,
    }
  }
  return { badge: { tone: 'good', text: 'No modeled shortfall' }, title }
}

// The full screen board for one plan's metrics, in reading order. `applicable`
// screens are the ones worth rendering: cross-testing, the top-heavy minimum,
// and the cash-balance pattern only appear once the design reaches them.
//
// `tooltip` is the long-form definition of the screen; `remediationId` opts the
// screen into the shared why/how-to-address copy in lib/remediation.js.
export const buildComplianceScreens = (metrics) => {
  const screens = [
    {
      id: 'adp',
      label: 'ADP screen',
      field: 'informalAdpHCE',
      format: 'percent',
      applicable: true,
      available: Boolean(metrics?.informalAdpAvailable),
      remediationId: 'adp',
      screen: buildAdpScreen(metrics),
      tooltip: 'Informal current-year ADP-style screen: average HCE base deferral rate vs the basic permitted limit derived from the NHCE average. A qualifying safe-harbor design may receive deemed-satisfaction treatment, subject to its terms and operation. This is not a formal ADP test; the administrator confirms the testing population, election, compensation definition, exclusions, corrections, and plan terms.',
    },
    {
      id: 'acp',
      label: 'ACP screen',
      field: 'informalAcpHCE',
      format: 'percent',
      applicable: true,
      available: Boolean(metrics?.informalAcpAvailable),
      remediationId: 'acp',
      screen: buildAcpScreen(metrics),
      tooltip: 'Informal current-year ACP-style screen on employer match plus employee after-tax contributions. A qualifying safe-harbor match may receive deemed-satisfaction treatment for the match portion, while employee after-tax money remains subject to ACP testing. Not a formal ACP test.',
    },
    {
      id: 'coverage',
      label: 'Coverage §410(b)',
      field: 'informalCoverageRatio',
      format: 'percent',
      applicable: true,
      available: Boolean(metrics?.informalCoverageAvailable),
      remediationId: 'coverage',
      screen: buildCoverageScreen(metrics),
      tooltip: 'Ratio-percentage screen on the non-excludable population (employees who failed plan age/service eligibility, and LTPT deferral-only employees, are excluded). The model uses 70% as the ratio-percentage threshold; results below it require review under the average benefit test (nondiscriminatory classification plus the average-benefit-percentage test). Not a formal coverage test.',
    },
    {
      id: 'cross-testing',
      label: 'Cross-testing §401(a)(4)',
      field: 'crossTestingFailingRateGroupCount',
      format: 'number',
      applicable: Boolean(metrics?.crossTestingApplicable),
      available: Boolean(metrics?.crossTesting?.available),
      remediationId: 'cross-testing',
      screen: buildCrossTestingScreen(metrics),
      tooltip: 'Modeled §401(a)(4) general-test screen for the employer nonelective/profit-sharing allocation: each allocation is converted to an equivalent benefit accrual rate (EBAR) at a standard testing age and interest rate, and each HCE’s rate group is compared with §410(b) thresholds. The model also evaluates the minimum allocation gateway (lesser of 5% or 1/3 of the highest HCE rate). Uses documented interest/mortality assumptions, not the plan’s actual actuarial elections. Not a formal test; confirm with the plan’s actuary or TPA.',
    },
    {
      id: 'top-heavy',
      label: 'Top-heavy indicator',
      field: 'topHeavyRatio',
      format: 'percent',
      applicable: true,
      available: Boolean(metrics?.topHeavyAvailable),
      remediationId: null,
      screen: buildTopHeavyScreen(metrics),
      tooltip: 'A modeled ratio of 60%+ of total balances belonging to key employees can indicate potential top-heavy status. Every employee included in plan metrics needs an explicit Y or N key status. Actual status and any minimum contribution requirements should be confirmed with the plan administrator or advisor.',
    },
    {
      id: 'top-heavy-min',
      label: 'Top-heavy minimum',
      field: 'topHeavyMinShortfall',
      format: 'currency',
      applicable: Boolean(metrics?.isTopHeavy && (metrics?.topHeavyAvailable || metrics?.cashBalanceEnabled)),
      available: Boolean(metrics?.topHeavyAvailable),
      remediationId: 'top-heavy-min',
      screen: buildTopHeavyMinScreen(metrics),
      tooltip: metrics?.topHeavyMinBasis === 'db-dc-aggregation-m12'
        ? 'With a cash-balance plan in the aggregation group the DC-only rule does not apply: the "lesser of the highest key rate" exception in §416(c)(2)(B) is unavailable, and the anti-duplication safe harbor of §1.416-1 M-12 sets a 5% DC minimum for each non-key participant covered by both plans (3% for DC-only participants). Cash-balance pay credits do not count toward it. A 2% DB minimum accrual or a comparability demonstration may cost less but depends on the adopted accrual structure. Modeled estimate, not a determination.'
        : 'For a modeled top-heavy DC plan, each non-key participant generally needs the lesser of 3% of pay or the highest key-employee allocation rate. Modeled estimate, not a determination.',
    },
    {
      id: 'minimum-participation',
      label: 'Minimum participation §401(a)(26)',
      field: 'minimumParticipationBenefitingCount',
      format: 'number',
      applicable: Boolean(metrics?.minimumParticipationApplicable),
      available: Boolean(metrics?.minimumParticipationAvailable),
      remediationId: null,
      screen: buildMinimumParticipationScreen(metrics),
      tooltip: 'This screen estimates the §401(a)(26) participation requirement using the lesser of 50 employees or the greater of 40% of all employees or 2, counting only participants whose accrual appears meaningful. Each participant\'s pay credit is run through the cash-balance benefit engine and the year\'s accrual rate is compared with a 0.5%-of-pay meaningful-benefit threshold at testing age 65. The plan\'s adopted normal retirement age and the meaningful-benefit standard itself are facts-and-circumstances; confirm with the plan\'s actuary. Not a formal test.',
    },
    {
      id: 'cash-balance',
      label: 'Cash balance pattern',
      field: 'cashBalanceHceCreditShare',
      format: 'percent',
      applicable: Boolean(metrics?.cashBalanceEnabled),
      available: Boolean(metrics?.cashBalanceEnabled && metrics?.totalCashBalanceCredits > 0),
      remediationId: null,
      screen: buildCashBalanceAllocationScreen(metrics),
      tooltip: 'Directional screen for whether cash-balance pay credits are concentrated in HCE/key employees. This is not a formal defined-benefit coverage or nondiscrimination test.',
    },
  ]
  return screens.filter((entry) => entry.applicable)
}

// Worst tone across the applicable screens, used for the Checks verdict. Only
// `warning` and `danger` count as needing attention: a screen that cannot run
// for lack of data is reported as a data problem, not as a failing screen.
export const worstScreenTone = (screens) => {
  if (screens.some((entry) => entry.screen?.badge?.tone === 'danger')) return 'danger'
  if (screens.some((entry) => entry.screen?.badge?.tone === 'warning')) return 'warning'
  return 'good'
}
